import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { desktop, useAppStore } from "@/controller";
import { hasAccountIdentity } from "@/auth-identity";
import { useAuth } from "@/models/useAuth";
import {
  createDesktopAuthRequest,
  DESKTOP_AUTH_STORAGE_KEY,
} from "@/desktop-auth";
import { logClientEvent } from "@/telemetry";

export default function DesktopSignInPage() {
  const { t } = useTranslation();
  const token = useAppStore((state) => state.userToken);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (!desktop.isAvailable) {
      window.location.replace("/auth");
      return;
    }
    if (hasAccountIdentity(useAuth.currentAuthUser(token))) {
      window.location.replace("/home");
    }
  }, [token]);

  const openBrowser = async () => {
    setOpening(true);
    try {
      const request = await createDesktopAuthRequest();
      sessionStorage.setItem(DESKTOP_AUTH_STORAGE_KEY, JSON.stringify(request));
      const url = new URL("/desktop-connect", window.location.origin);
      url.searchParams.set("challenge", request.challenge);
      url.searchParams.set("state", request.state);
      logClientEvent("auth.handoffStarted", {
        method: "desktopBrowser",
        source: "desktopApp",
      });
      window.open(url.toString(), "_blank", "noopener");
    } catch {
      logClientEvent("auth.handoffFailed", {
        method: "desktopBrowser",
        reason: "requestCreation",
      });
    } finally {
      setOpening(false);
    }
  };

  if (!desktop.isAvailable) {
    return null;
  }

  return (
    <main className="app-page-background min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center px-6">
      <section className="w-full max-w-md text-center">
        <img src="/logo.png" alt="1Transcribe" className="mx-auto mb-6 h-16 w-16 rounded-2xl" />
        <h1 className="text-3xl font-bold tracking-tight">{t("desktopSignIn.title")}</h1>
        <p className="mt-3 text-[var(--text-secondary)]">{t("desktopSignIn.subtitle")}</p>
        <button
          data-action-id="auth.desktop.openBrowser"
          type="button"
          disabled={opening || !desktop.supportsDesktopAuth}
          onClick={() => void openBrowser()}
          className="mt-8 min-h-12 w-full rounded-xl bg-[var(--accent)] px-6 font-semibold text-[var(--text-inverse)] disabled:opacity-50"
        >
          {opening ? t("desktopSignIn.opening") : t("desktopSignIn.openBrowser")}
        </button>
        {!desktop.supportsDesktopAuth && (
          <p className="mt-4 text-sm text-[var(--text-secondary)]">{t("desktopSignIn.updateRequired")}</p>
        )}
      </section>
    </main>
  );
}
